/**
 * Company KYC Mock Data Panel
 *
 * 开发环境下的快捷填充工具
 * 提供可视化的面板用于快速填充企业KYC表单数据
 *
 * @module mockDataGenerator.demo
 */

import { Button } from "antd";
import { useState } from "react";
import {
  companyMockDataPresets,
  generateCompanyInfoMockData,
} from "./mockDataGenerator.company";

interface CompanyMockDataPanelProps {
  /** 企业信息表单实例 */
  form?: any;
}

/**
 * 企业KYC Mock 数据生成工具面板
 *
 * @example
 * ```tsx
 * import { MockDataPanel } from './mockDataGenerator.demo';
 *
 * export default function CompanyInfoPage() {
 *   const [form] = Form.useForm();
 *
 *   return (
 *     <>
 *       <YourForm form={form} />
 *       <MockDataPanel form={form} />
 *     </>
 *   );
 * }
 * ```
 */
export function CompanyMockDataPanel({ form }: CompanyMockDataPanelProps = {}) {
  const [visible, setVisible] = useState(false);
  const [generatedData, setGeneratedData] = useState<any>(null);

  /* ============================================================
   * 企业信息操作
   * ========================================================== */

  const handleFillBasic = () => {
    const data = generateCompanyInfoMockData();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（随机）:", data);
  };

  const handleFillHKListed = () => {
    const data = companyMockDataPresets.hkListedCompany();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（香港上市公司）:", data);
  };

  const handleFillHKPrivate = () => {
    const data = companyMockDataPresets.hkPrivateCompany();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（香港非上市公司）:", data);
  };

  const handleFillMainland = () => {
    const data = companyMockDataPresets.mainlandCompany();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（中国大陆公司）:", data);
  };

  const handleFillSingapore = () => {
    const data = companyMockDataPresets.singaporeCompany();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（新加坡公司）:", data);
  };

  const handleFillNonProfit = () => {
    const data = companyMockDataPresets.nonProfitOrg();
    form?.setFieldsValue(data);
    setGeneratedData(data);
    console.log("✅ 已填充企业信息（非营利组织）:", data);
  };

  const handleClear = () => {
    form?.resetFields();
    setGeneratedData(null);
    console.log("✅ 已清空表单");
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            🏢
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">🏢 企业KYC Mock工具</span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-2"
            style={{ maxHeight: "500px" }}
          >
            <Button size="small" block onClick={handleFillBasic}>
              随机企业数据
            </Button>
            <Button size="small" block onClick={handleFillHKListed}>
              香港上市公司
            </Button>
            <Button size="small" block onClick={handleFillHKPrivate}>
              香港非上市公司
            </Button>
            <Button size="small" block onClick={handleFillMainland}>
              中国大陆公司
            </Button>
            <Button size="small" block onClick={handleFillSingapore}>
              新加坡公司
            </Button>
            <Button size="small" block onClick={handleFillNonProfit}>
              非营利组织
            </Button>
          </div>

          {/* 清空按钮 */}
          <div className="px-4 pb-4 pt-2 border-t">
            <Button danger size="small" block onClick={handleClear}>
              清空表单
            </Button>
          </div>

          {/* 生成的数据预览（可选） */}
          {generatedData && (
            <div className="px-4 pb-4">
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-500 hover:text-gray-700">
                  查看生成的数据
                </summary>
                <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto max-h-40">
                  {JSON.stringify(generatedData, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default CompanyMockDataPanel;
